import { CommonModule } from '@angular/common';
import {
  Component,
  inject,
  Input,
  TemplateRef,
  ChangeDetectionStrategy
} from '@angular/core';
import {
  TranslateModule,
  TranslateService,
} from '@ngx-translate/core';
import { AVATAR_WIDTH } from '../../../constants';
import { TranslationModule } from '../../../i18n/translation-module';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { ActivityInterface } from '@vendasta/crm';
import { RxState } from '@rx-angular/state';
import { GalaxyAvatarModule } from '@vendasta/galaxy/avatar';
import {
  combineLatest,
  filter,
  map,
  tap,
} from 'rxjs';
import {
  CrmFieldService,
  SystemFieldIds,
} from '@galaxy/crm/services/field-service';
import { User } from '@vendasta/iamv2';
import { DateFormat } from '@vendasta/galaxy/utility/date-utils';
import { CrmUnknownUserAvatarComponent } from '../../unknown-user-avatar/unknown-user-avatar.component';

import { ActivityAssociationService } from './activity-association.service';
import { RouterLink } from '@angular/router';

@Component({
  selector: 'crm-activity-template',
  templateUrl: './activity-template.component.html',
  styleUrls: ['./activity-template.component.scss'],
  providers: [RxState],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CommonModule,
    TranslateModule,
    TranslationModule,
    MatCardModule,
    MatIconModule,
    GalaxyAvatarModule,
    CrmUnknownUserAvatarComponent,
    RouterLink,
  ],
})
export class CrmActivityTemplateComponent {
  AVATAR_WIDTH = AVATAR_WIDTH;
  dateFormat = DateFormat.medium;

  @Input() set activity(activity: ActivityInterface) {
    this.state.set({ activity: activity });
  }
  get activity() {
    return this.state.get().activity;
  }

  @Input() set ownerUser(ownerUser: User | undefined) {
    this.state.set({ ownerUser: ownerUser });
  }
  get ownerUser() {
    return this.state.get().ownerUser;
  }

  @Input() set isOwnedByServiceAccount(isOwnedByServiceAccount: boolean) {
    this.state.set({ isOwnedByServiceAccount: isOwnedByServiceAccount });
  }
  get isOwnedByServiceAccount() {
    return this.state.get().isOwnedByServiceAccount;
  }

  @Input() set headerText(text: string) {
    this.state.set({ headerText: text });
  }
  get headerText() {
    return this.state.get().headerText;
  }

  private _showAvatar = true;
  @Input() set showAvatar(showAvatar: boolean) {
    this._showAvatar = showAvatar;
  }
  get showAvatar() {
    return this._showAvatar;
  }

  // if this is set, the fullName will be displayed before the headerText
  // e.g. "John Doe sent an email"
  @Input() showFullName = true;

  // custom template to render in the subtitle section, replacing the formatted date
  @Input() subtitleTemplate?: TemplateRef<unknown>;

  @Input() showPrimaryAssociation = false;

  unknownNamePlaceholder = this.translateService.instant('CONTACT.ACTIVITIES.UNKNOWN_NAME');
  fullName$ = this.state.select('fullName');
  readonly headerText$ = this.state
    .select('activity')
    .pipe(map((activity) => this.headerTextFromActivityType(activity?.crmObjectSubtype)));
  effectiveDate$ = this.state.select('effectiveDate');

  showUnknownAvatar$ = combineLatest([
    this.state.select('isUnknownUser'),
    this.state.select('isOwnedByServiceAccount'),
  ]).pipe(map(([isUnknownUser, isOwnedByServiceAccount]) => isUnknownUser || isOwnedByServiceAccount));

  profileImageUrl$ = this.state.select('ownerUser').pipe(map((user) => user?.profileImageUrl ?? ''));

  private readonly associationService = inject(ActivityAssociationService);
  primaryAssociation = this.associationService.primaryAssociation;
  routePrefix = this.associationService.routePrefix;

  constructor(
    private readonly state: RxState<ComponentState>,
    private readonly fieldService: CrmFieldService,
    private readonly translateService: TranslateService,
  ) {
    this.state.set({
      isOwnedByServiceAccount: false,
      ownerUser: undefined,
      activity: undefined,
      fullName: this.unknownNamePlaceholder,
      isUnknownUser: false,
      effectiveDate: null,
    });

    this.state.connect(
      'fullName',
      this.state.select('ownerUser').pipe(
        filter((user): user is User => user !== null),
        map((user) => `${user.firstName || ''} ${user.lastName || ''}`.trim()),
        tap((fullName) => this.state.set({ isUnknownUser: fullName === '' })),
        map((fullName) => fullName || this.unknownNamePlaceholder),
      ),
    );

    this.state.connect(
      'effectiveDate',
      this.state
        .select('activity')
        .pipe(
          map(
            (activity) =>
              this.fieldService.getFieldValueFromCrmObject(activity, SystemFieldIds.ActivityEffectiveDate)?.dateValue ??
              null,
          ),
        ),
    );
  }

  private headerTextFromActivityType(activityType?: string): string {
    switch (activityType) {
      case 'Note':
        return 'CONTACT.ACTIVITIES.HEADER_ADDITIONAL_TEXT.NOTE';
      case 'Email':
        return 'CONTACT.ACTIVITIES.HEADER_ADDITIONAL_TEXT.LOGGED_EMAIL';
      case 'Call':
        return 'CONTACT.ACTIVITIES.HEADER_ADDITIONAL_TEXT.LOGGED_CALL';
      case 'Meeting':
        return 'CONTACT.ACTIVITIES.HEADER_ADDITIONAL_TEXT.LOGGED_MEETING';
      case 'Task':
        return 'CONTACT.ACTIVITIES.HEADER_ADDITIONAL_TEXT.TASK';
      case 'Communication':
        return 'CONTACT.ACTIVITIES.HEADER_ADDITIONAL_TEXT.LOGGED_COMMUNICATION';
      default:
        return '';
    }
  }
}

interface ComponentState {
  isOwnedByServiceAccount: boolean;
  ownerUser?: User;

  activity: ActivityInterface;

  fullName: string;
  headerText: string;
  isUnknownUser: boolean;
  effectiveDate: Date | null;
}
